import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import CompUtils from '@/models/comp/CompUtils';
import ICompDatas from '@/models/comp/ICompDatas';
import { formFieldsStoreV36, Getters, Mutations, store } from '@/store';
import { newUpsertCompDataRequestByFieldName } from '@/store/3.6/models/IUpsertCompDataRequest';
import QSUtils from '@/utils/QSUtils';
import GridColumnUtils from '@/utils/GridColumnUtils';
import CompTranslationsManager from './CompTranslationsManager';
import FormsManager from './FormsManager';
import UserSettingsManager from '../UserSettingsManager';

/**
 * Manages comp priorities (aka preferences, aka cascades).
 */
export default {
    /**
     * Run priorities on a field on all comps, identified by the given field id.
     *
     * @param fieldId
     */
    async runForAllCompsOnField(fieldId: number) {
        const allComps = store.getters[Getters.CompAddressesV36.Comps];
        for (const comp of allComps) {
            if (comp.compNumber === 0 && UserSettingsManager.isSubjectCascadeDisabled()) {
                // Skip if Subject transfer column is locked
                continue;
            }

            // Skip if field value has the initial of 'ff'
            if (checkCompForFFDataByFieldId(comp, fieldId)) {
                continue;
            }

            await this.runOnField(comp, fieldId);
            await CompTranslationsManager.runOnField(comp, fieldId);
        }
    },

    /**
     * Run priorities on a field identified by the given comp and field id.
     * @param comp
     * @param fieldId
     */
    async runOnField(comp: IComp, fieldId: number): Promise<void> {
        const compSourceDatas: ICompDatas = comp.sourceData ?? [];
        const formFields = await FormsManager.getFormFields();

        const fieldData = formFields.find((ff) => ff.fieldId === fieldId);
        if (fieldData) {

            // Clear sourceInitial and value based on fieldId.  Then, it will recalculate
            const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
            request.sourceInitial = '';
            request.value = '';
            request.fieldId = fieldId;
            request.description = 'CompPrioritiesManager.runOnField()';
            store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);

            //Loop through Data Priorities
            const flattenedDataPriorities = fieldData.flattenedDataPriorities.split(' | ');
            for (const source in flattenedDataPriorities) {
                let sourceInitial = flattenedDataPriorities[source];
                if (!sourceInitial) {
                    continue;
                }

                // Custom Value check
                if (sourceInitial.toLowerCase().includes(SourceDataInitials.CustomValue)) {
                    sourceInitial = sourceInitial.replaceAll('"', '');
                    const cvParts = sourceInitial.split(/(?<=^\S+)\s/);

                    const cvIdx = compSourceDatas.findIndex((cd) => cd.fieldName === fieldData.fieldName);
                    if (cvIdx !== -1 && cvParts.length > 1 && cvParts[1] !== '') {
                        cvParts[1] = cvParts[1].replace(/["]+/g, '');

                        const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
                        request.fieldId = fieldId;
                        request.fieldRulesApplied = false;
                        request.sourceInitial = SourceDataInitials.CustomValue;
                        request.value = cvParts[1];
                        //request.description = 'CompPrioritiesManager.runOnField(CV)';
                        // console.log(`CompPrioritiesManager.runOnField(), Upserting field[${request.fieldId},${request.fieldName}], initial[${request.sourceInitial}], value[${request.value}]`);
                        store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);
                        break;
                    }
                }

                const feedData = CompUtils.getDataSourceFeedData(comp, sourceInitial);
                if (feedData && feedData.sourceData) {
                    const sourceFieldName = getSourceFieldName(feedData.sourceData, fieldData.fieldName);
                    if (sourceFieldName) {
                        const feedDataValue = feedData.sourceData[sourceFieldName];
                        if (feedDataValue !== '' && feedDataValue !== null) {
                            const itemIdx = compSourceDatas.findIndex((cd) => cd.fieldName === fieldData.fieldName);
                            if (itemIdx !== -1) {
                                const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
                                request.fieldId = fieldId;
                                request.fieldRulesApplied = false;
                                request.sourceInitial = sourceInitial.toLowerCase();
                                request.value = feedDataValue;
                                //request.description = `CompPrioritiesManager.runOnField(${sourceInitial})`;
                                // console.log(`CompPrioritiesManager.runOnField(), Upserting field[${request.fieldId},${request.fieldName}], initial[${request.sourceInitial}], value[${request.value}]`);
                                store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);                                
                                break;
                            }
                        }
                    } else {

                        // Process the the repeatable feed fields (e.g. VEHICLE_STORAGE-1, VEHICLE_STORAGE-2) that match the 
                        // base field name but aren't an exact match and only if the base field name doesn't have a value in 
                        // the feed data.
                        const repeatableFields = getRepeatableFeedFieldsByBaseFieldName(feedData, fieldData.fieldName);
                        if(repeatableFields.length > 0) {
                            let hasAppliedRepeatableValue = false;

                            repeatableFields.forEach((field) => {
                                if (field.value === '' || field.value === null || field.value === undefined) {
                                    return;
                                }

                                // Index 0 should populate the base transfer row (FIELDNAME), not FIELDNAME-0.
                                const targetFieldName = field.repeatIndex === 0 ? fieldData.fieldName : field.fieldName;
                                const request = newUpsertCompDataRequestByFieldName(comp, targetFieldName);

                                // Use decimal fieldId for repeat index > 0 and keep base fieldId for index 0.
                                request.fieldId = field.repeatIndex === 0
                                    ? fieldData.fieldId
                                    : Number(`${fieldData.fieldId}.${field.repeatIndex}`);
                                request.fieldRulesApplied = false;
                                request.sourceInitial = sourceInitial.toLowerCase();
                                request.value = field.value;

                                store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);
                                hasAppliedRepeatableValue = true;
                            });

                            if (hasAppliedRepeatableValue) {
                                // Respect source priority order by stopping after the first source
                                // that provides repeatable values for this field.
                                break;
                            }
                        }
                    }
                }
            }
        }
    },

    /**
     * Persist flattened repeatable values for one form field before its rules run.
     * This intentionally does not run during the all-fields startup cascade because
     * committing every flattened field individually causes excessive reactive rebuilds.
     */
    async syncFlattenedRepeatableField(comp: IComp, fieldId: number): Promise<void> {
        const formFields = await FormsManager.getFormFields();
        const fieldData = formFields.find((ff) => ff.fieldId === fieldId);
        if (!fieldData) {
            return;
        }

        const flattenedDataPriorities = getFlattenedSourceInitials(
            comp,
            fieldData.fieldName,
            fieldData.flattenedDataPriorities.split(' | ')
        );
        for (let sourceInitial of flattenedDataPriorities) {
            if (!sourceInitial || sourceInitial.toLowerCase().includes(SourceDataInitials.CustomValue)) {
                continue;
            }

            const feedData = CompUtils.getDataSourceFeedData(comp, sourceInitial);
            const repeatableFields = getFlattenedRepeatableFeedFieldsByBaseFieldName(feedData, fieldData.fieldName);
            let synchronizedValue = false;

            for (const field of repeatableFields) {
                const childUpdates = getFlattenedChildUpdates(field, feedData, sourceInitial);
                const isNestedSubjectField = childUpdates.length > 0;
                if (!isNestedSubjectField && (field.value === '' || field.value === null || field.value === undefined)) {
                    continue;
                }

                const targetFieldName = field.fieldName;
                if (hasNonBlankFormFillerValue(comp, targetFieldName)) {
                    synchronizedValue = true;
                    continue;
                }

                const request = newUpsertCompDataRequestByFieldName(comp, targetFieldName);
                request.fieldId = field.repeatIndex === 0
                    ? fieldData.fieldId
                    : Number(`${fieldData.fieldId}.${field.repeatIndex}`);
                request.fieldRulesApplied = false;
                request.sourceInitial = hasSourceValue(field.value) ? sourceInitial.toLowerCase() : '';
                request.value = field.value;
                request.description = 'CompPrioritiesManager.syncFlattenedRepeatableField()';

                store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);
                if (childUpdates.length > 0) {
                    const allowedChildUpdates = childUpdates.filter(
                        (update: any) => !hasNonBlankFormFillerValue(comp, update.fieldName)
                    );
                    store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataBatchByFieldName, {
                        comp: comp,
                        updates: allowedChildUpdates,
                    });
                }
                synchronizedValue = true;
            }

            if (synchronizedValue) {
                break;
            }
        }
    },

    /**
     * Persist all flattened repeatable values with one store mutation before translations run.
     * Batching prevents the active-comp deep watcher from rebuilding once per repeated value.
     */
    async syncAllFlattenedRepeatableFields(comp: IComp): Promise<void> {
        const formFields = await FormsManager.getFormFields();
        const applicableFormFields = comp.isSubject
            ? formFields.filter((fieldData: any) => fieldData?.isSubjectField === true)
            : formFields.filter((fieldData: any) => fieldData?.isSubjectField !== true);
        const updates: any[] = [];

        for (const fieldData of applicableFormFields) {
            const flattenedDataPriorities = getFlattenedSourceInitials(
                comp,
                fieldData.fieldName,
                fieldData.flattenedDataPriorities.split(' | ')
            );
            for (const sourceInitial of flattenedDataPriorities) {
                if (!sourceInitial || sourceInitial.toLowerCase().includes(SourceDataInitials.CustomValue)) {
                    continue;
                }

                const feedData = CompUtils.getDataSourceFeedData(comp, sourceInitial);
                const repeatableFields = getFlattenedRepeatableFeedFieldsByBaseFieldName(feedData, fieldData.fieldName);
                let foundValue = false;

                for (const field of repeatableFields) {
                    const childUpdates = getFlattenedChildUpdates(field, feedData, sourceInitial);
                    const isNestedSubjectField = childUpdates.length > 0;
                    if (!isNestedSubjectField && (field.value === '' || field.value === null || field.value === undefined)) {
                        continue;
                    }

                    const targetFieldName = field.fieldName;
                    const fieldId = field.indices.every((index: number) => index === 0)
                        ? fieldData.fieldId
                        : Number(`${fieldData.fieldId}.${field.indices.join('')}`);
                    const current = comp.sourceData?.find((compData) => compData.fieldName === targetFieldName);
                    if (current?.sourceInitial === SourceDataInitials.UserOverride) {
                        continue;
                    }

                    if (hasNonBlankFormFillerValue(comp, targetFieldName)) {
                        // The container being user-entered means this whole repeat entry is
                        // user-owned - do not let sibling child fields (e.g. DETAIL_AREA/
                        // DETAIL_FEATURE) get backfilled from an unrelated PR entry at the same
                        // index just because FormFiller never happened to set those specific
                        // sub-fields (confirmed: this mixed a FormFiller "Deck" with PR's "Porch"
                        // detail fields at the same AmenityCategory-0 entry).
                        foundValue = true;
                        continue;
                    }

                    const normalizedSourceInitial = hasSourceValue(field.value) ? sourceInitial.toLowerCase() : '';
                    if (
                        current?.fieldId === fieldId &&
                        current?.value === field.value &&
                        current?.sourceInitial === normalizedSourceInitial
                    ) {
                        updates.push(...childUpdates.filter(
                            (update: any) => !hasNonBlankFormFillerValue(comp, update.fieldName)
                        ));
                        foundValue = true;
                        continue;
                    }

                    updates.push({
                        fieldName: targetFieldName,
                        fieldId: fieldId,
                        fieldRulesApplied: false,
                        sourceInitial: normalizedSourceInitial,
                        value: field.value,
                    });
                    updates.push(...childUpdates.filter(
                        (update: any) => !hasNonBlankFormFillerValue(comp, update.fieldName)
                    ));
                    foundValue = true;
                }

                if (foundValue) {
                    break;
                }
            }
        }

        if (updates.length > 0) {
            store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataBatchByFieldName, {
                comp: comp,
                updates: updates,
            });
        }
    },
};

function hasNonBlankFormFillerValue(comp: IComp, fieldName: string): boolean {
    const compData = comp.sourceData?.find((item) => item.fieldName === fieldName);
    return String(compData?.sourceInitial || '').toLowerCase() === SourceDataInitials.FormFiller &&
        String(compData?.value ?? '').trim() !== '';
}

function checkCompForFFDataByFieldId(comp: IComp, fieldId: number): boolean {
    if (comp.sourceData) {
        //const compSourceData = comp.sourceData ? _.cloneDeep(comp.sourceData) : [];
        const compData = comp.sourceData.find((f: any) => f.fieldId === fieldId);
        if (compData) {
            const sourceInitial = compData.sourceInitial.toLowerCase();
            if (sourceInitial === SourceDataInitials.FormFiller || (sourceInitial === SourceDataInitials.None && compData.value !== '')) {
                return true;
            }
        }
    }

    return false;
}

function getSourceFieldName(sourceData: Record<string, any>, fieldName: string): string | undefined {
    const subjectFlattenedFieldCollections: Record<string, string> = {
        PROJECT_TYPE: 'ProjectType',
        NATAMLAND: 'NativeAmericanLands',
        SITE_OWNED_IN_COMMON: 'SiteOwnedInCommon',
        HORESPMAINT: 'HomeownerResponsible',
        NEWCONST: 'NewConstruction',
    };
    const baseFieldName = fieldName.split('-').pop() || fieldName;
    const unflattenedFieldName = baseFieldName.replace(/_FLATTENED$/, '');
    const collectionName = subjectFlattenedFieldCollections[unflattenedFieldName];
    const candidates = [fieldName];

    if (collectionName) {
        const flattenedFieldName = `${unflattenedFieldName}_FLATTENED`;
        candidates.unshift(
            `${collectionName}-${flattenedFieldName}`,
            `${collectionName}-0-${flattenedFieldName}`,
            flattenedFieldName
        );
    }

    return candidates.find((candidate) => QSUtils.isKeyInObject(sourceData, candidate));
}

function getFlattenedSourceInitials(comp: IComp, fieldName: string, sourceInitials: string[]): string[] {
    const unflattenedFieldName = fieldName.replace(/_FLATTENED$/, '');
    const transferFieldNames = new Set([
        fieldName,
        unflattenedFieldName,
        `${unflattenedFieldName}_FLATTENED`,
    ]);
    const selectedTransferData = comp.sourceData?.find((compData) =>
        transferFieldNames.has(compData.fieldName) &&
        hasSourceValue(compData.value) &&
        String(compData.sourceInitial || '').trim() !== ''
    );
    const selectedSourceInitial = String(selectedTransferData?.sourceInitial || '').toLowerCase();
    if (!selectedSourceInitial) {
        return sourceInitials;
    }

    const selectedSource = sourceInitials.find(
        (sourceInitial) => String(sourceInitial || '').toLowerCase() === selectedSourceInitial
    );
    if (!selectedSource) {
        return sourceInitials;
    }

    return [
        selectedSource,
        ...sourceInitials.filter((sourceInitial) => sourceInitial !== selectedSource),
    ];
}

function hasSourceValue(value: any): boolean {
    return value !== '' && value !== null && value !== undefined;
}

function getRepeatableFeedFieldsByBaseFieldName(feedData: any, baseFieldName: string): any[] {
    if (!feedData || !baseFieldName) {
        return [];
    }

    const result: any[] = [];
    const repeatableFieldPattern = new RegExp(`^${baseFieldName}-(\\d+)$`);

    for (const fieldName of Object.keys(feedData.sourceData)) {
        const match = fieldName.match(repeatableFieldPattern);
        if (!match) {
            continue;
        }

        result.push({
            sourceInitial: feedData.sourceInitial,
            fieldName: fieldName,
            repeatIndex: Number(match[1]),
            value: feedData.sourceData[fieldName],
        });
    }


    return result;
}

function getFlattenedRepeatableFeedFieldsByBaseFieldName(feedData: any, baseFieldName: string): any[] {
    if (!feedData?.sourceData || !baseFieldName) {
        return [];
    }

    const result: any[] = [];
    for (const fieldName of Object.keys(feedData.sourceData)) {
        const baseFieldSuffix = `-${baseFieldName}`;
        const flattenedFieldSuffix = `-${baseFieldName}_FLATTENED`;
        const matchingSuffix = fieldName.endsWith(flattenedFieldSuffix)
            ? flattenedFieldSuffix
            : fieldName.endsWith(baseFieldSuffix)
                ? baseFieldSuffix
                : '';
        if (!matchingSuffix) {
            continue;
        }

        const pathParts = fieldName
            .slice(0, -matchingSuffix.length)
            .split('-');
        const firstIndexPart = pathParts.find((part) => /^\d+$/.test(part));
        const indices = pathParts.filter((part) => /^\d+$/.test(part)).map(Number);

        result.push({
            fieldName: fieldName,
            repeatIndex: firstIndexPart === undefined ? 0 : Number(firstIndexPart),
            indices: indices,
            value: feedData.sourceData[fieldName],
        });
    }

    return result;
}

function getFlattenedChildUpdates(parentField: any, feedData: any, sourceInitial: string): any[] {
    const childFieldNames = GridColumnUtils.getParentChildFields(parentField.fieldName);
    if (childFieldNames.length === 0 || !feedData?.sourceData) {
        return [];
    }

    const parentBaseFieldName = String(parentField.fieldName).split('-').pop() || '';
    const parentPrefix = String(parentField.fieldName).slice(0, -(parentBaseFieldName.length + 1));
    if (!parentPrefix) {
        return [];
    }

    const additionalDetails = formFieldsStoreV36.FormFieldsAdditionalDetails || [];
    const updates: any[] = [];
    for (const childFieldName of childFieldNames) {
        const groupedChildFieldName = `${parentPrefix}-${childFieldName}`;
        const childValue = feedData.sourceData[groupedChildFieldName];
        if (childValue === undefined || childValue === null) {
            continue;
        }

        const childFieldData = additionalDetails.find((field: any) => field.fieldName === childFieldName);
        updates.push({
            fieldName: groupedChildFieldName,
            fieldId: childFieldData?.fieldId,
            fieldRulesApplied: false,
            sourceInitial: hasSourceValue(childValue) ? sourceInitial.toLowerCase() : '',
            value: childValue,
        });
    }

    return updates;
}